import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TopBar from './layout/TopBar';
import BottomNav from './BottomNav';
import Phone from './Phone';
import { C } from './theme';
import { fmtCurrency, pick, useDrillAuth } from './drillHelpers';
import HourlyBarChart from './reports/components/charts/reusable/HourlyBarChart';
import SalesWaterfall, { type WaterfallSource, buildWaterfallData, getTotalPaidValue, getTotalPaidLabel, getTotalPaidSub } from './reports/components/SalesWaterfall';
import { NetSalesShimmer, Bone } from './Shimmer';
import { useDesktopTopBar } from '../context/DesktopLayoutContext';
import { useIsDesktop } from './reports/reportTokens';
import { getSalesSummary, todayStr, yesterdayStr, getStartEndFromFilter } from '../services/reportsApi';
import { useStoreTimeZone } from '../utils/dateTimeUtils';
import { useAppSelector, useAppDispatch } from '../redux/hooks';
import { fetchHourlySalesRequest } from '../redux/reports/clickHouse/clickHouseActions';
import type { HourlySalesResponse } from '../interface/clickHouseInterface';
import { exportPeriodFor } from '../utils/reportPeriod';

const DrillNetSales = ({ onBack, initialDateFilter, initialDate, snapshotValues: snapshotValuesProp, snapshotKpisLoading, orderSource, onDeductionClick, scrollToKpiOnMount }: { onBack?: () => void; initialDateFilter?: string; initialDate?: string; snapshotValues?: any; snapshotKpisLoading?: boolean; orderSource?: 'direct' | 'thirdparty'; onDeductionClick?: (key: string) => void; scrollToKpiOnMount?: boolean } = {} as any) => {
    const isEmbedded = !!onBack;
    const navigate = useNavigate();
    // SW3 — embedded (from Snapshot mobile) gets a callback that switches the parent's own
    // drillView; standalone (this screen's own route) navigates to Snapshot with a deep link
    // since it has no sibling drill views of its own to switch to.
    const handleDeductionClick = onDeductionClick ?? ((key: string) => navigate(`/reports/snapshot?drill=${key}`));
    const isDesktop = useIsDesktop();
    // Scroll the KPI row into view when the parent tells us this mount is a return from a
    // deduction drill (this component fully remounts each time drillView comes back to
    // 'net-sales', since a different sibling component occupies that slot in between).
    const kpiRowRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!scrollToKpiOnMount) return;
        requestAnimationFrame(() => kpiRowRef.current?.scrollIntoView({ behavior: 'auto', block: 'start' }));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!isEmbedded && isDesktop) navigate('/reports/snapshot', { replace: true });
    }, [isDesktop, navigate, isEmbedded]);
    const setTopBarProps = useDesktopTopBar();
    const dispatch = useAppDispatch();
    const { locationId, token, snapshotValues: snapshotValuesFromAuth } = useDrillAuth();
    const snapshotValues = snapshotValuesProp ?? snapshotValuesFromAuth;
    const storeTz = useStoreTimeZone();
    const today = useMemo(() => todayStr(storeTz), [storeTz]);
    const yesterday = useMemo(() => yesterdayStr(storeTz), [storeTz]);
    const [selectedDate, setSelectedDate] = useState(initialDate ?? today);
    const [activeDateFilter, setActiveDateFilter] = useState(initialDateFilter ?? 'Today');
    const isToday = activeDateFilter === 'Today';
    const dateRange = useMemo(() => {
        const range = getStartEndFromFilter(activeDateFilter, today, yesterday);
        return range || { startDate: selectedDate, endDate: selectedDate };
    }, [activeDateFilter, selectedDate, today, yesterday]);

    // Unified hourly source — same Redux state + endpoint as the desktop Net Sales drill
    // (fetchHourlySalesRequest handles Today → live, historical → date-range, both via /sales/live/v2/hourly-sales).
    const hourlySalesData: HourlySalesResponse | null = useAppSelector((s) => s.clickHouse.hourlySalesData);
    const hourlySalesLoading: boolean = useAppSelector((s) => s.clickHouse.hourlySalesLoading);
    const hourlySalesFailure: boolean = useAppSelector((s) => s.clickHouse.hourlySalesFailure);
    const snapshotDetailsLoading: boolean = useAppSelector((s) => s.clickHouse.snapshotDetailsLoading);
    // While snapshot-details refetches (date/channel change), snapshotValues.netSales holds the
    // previous range's value — shimmer the KPI instead of flashing stale data (SSE-18).
    // Embedded gets the parent's flag (also covers its legacy-summary fallback window);
    // standalone keys off the redux flag directly.
    const kpisLoading = snapshotKpisLoading ?? snapshotDetailsLoading;
    // SW 10: the waterfall's Voids/Complimentary/Refunds rows are fed by three separate async
    // fetches on top of snapshot-details — the parent's kpisLoading flag alone drops before
    // they land, so the bars would render stale/zero values then jump once each one resolves.
    const liveVoidSummaryLoading: boolean = useAppSelector((s) => s.clickHouse.liveVoidSummaryLoading);
    const liveCompOrderLogLoading: boolean = useAppSelector((s) => s.clickHouse.liveCompOrderLogLoading);
    const liveRefundSummaryLoading: boolean = useAppSelector((s) => s.clickHouse.liveRefundSummaryLoading);
    const waterfallLoading = kpisLoading || liveVoidSummaryLoading || liveCompOrderLogLoading || liveRefundSummaryLoading;

    // Standalone (non-embedded) view fetches its own hourly data. When embedded, the parent
    // SnapshotScreen already dispatches the same request — avoid a duplicate call.
    useEffect(() => {
        if (!isEmbedded && locationId) {
            dispatch(fetchHourlySalesRequest({ locationId, startDate: dateRange.startDate, endDate: dateRange.endDate }));
        }
    }, [isEmbedded, locationId, dateRange.startDate, dateRange.endDate]); // eslint-disable-line react-hooks/exhaustive-deps

    const [loading, setLoading] = useState(true);

    const handleDateFilterChange = useCallback((opt: string) => {
        setActiveDateFilter(opt);
        if (opt === 'Today') setSelectedDate(today);
        else if (opt === 'Yesterday') setSelectedDate(yesterday);
    }, [today, yesterday]);

    useEffect(() => {
        if (isEmbedded) return;
        setTopBarProps({
            title: 'Net Sales',
            onBack: () => navigate('/reports/snapshot'),
            dateFilters: { value: activeDateFilter, onChange: handleDateFilterChange },
        });
    }, [setTopBarProps, activeDateFilter, handleDateFilterChange, navigate, isEmbedded]);

    const [summary, setSummary] = useState<any>(null);

    useEffect(() => {
        if (!locationId) {
            setLoading(false);
            return;
        }
        // Embedded view: snapshotValues.netSales already comes from clickhouse — skip non-clickhouse fetch
        if (isEmbedded) {
            setLoading(false);
            return;
        }
        const opts = { locationId, startDate: dateRange.startDate, endDate: dateRange.endDate, token };
        setLoading(true);
        getSalesSummary({ ...opts })
            .then((r) => setSummary(r ?? null))
            .catch(() => setSummary(null))
            .finally(() => setLoading(false));
    }, [locationId, token, dateRange.startDate, dateRange.endDate, isToday, isEmbedded]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Hourly Revenue (itemTotal) — unified for Today and historical ──
    const hourlyHours = hourlySalesData?.hours ?? [];
    const operatingStartHour = hourlySalesData?.operatingStartHour ?? 6;
    const operatingEndHour = hourlySalesData?.operatingEndHour ?? 23;
    const operatingHourCount = hourlySalesData?.operatingHourCount ?? 0;
    const hourlyRevenueChartData: { label: string; value: number }[] = hourlyHours.map(h => {
        const suffix = h.hourValue >= 12 ? ' PM' : ' AM';
        const disp = h.hourValue % 12 || 12;
        return { label: `${disp}${suffix}`, value: h.itemTotal };
    });
    const revenuePeakValue = hourlyRevenueChartData.reduce((m, d) => Math.max(m, d.value), 0);
    const revenuePeakIndex = revenuePeakValue > 0 ? hourlyRevenueChartData.findIndex(d => d.value === revenuePeakValue) : -1;
    const peakHour = revenuePeakIndex >= 0 ? (hourlyRevenueChartData[revenuePeakIndex]?.label ?? '--') : '--';
    const windowHours = hourlyHours.filter(h => h.hourValue >= operatingStartHour && h.hourValue <= operatingEndHour);
    const avgPerHour = operatingHourCount > 0
        ? windowHours.reduce((s, h) => s + h.itemTotal, 0) / operatingHourCount
        : 0;
    const hourlyTotal = hourlyRevenueChartData.reduce((s, d) => s + (Number(d.value) || 0), 0);

    const apiNetSales = pick(summary, 'totalNetSales', 'netSales', 'totalSales', 'net_sales', 'total_sales', 'revenue');
    const apiNetSalesNum = apiNetSales != null && apiNetSales !== '' ? Number(apiNetSales) : null;
    const snapshotNetSales = snapshotValues?.netSales != null ? Number(snapshotValues.netSales) : null;
    // hourlyTotal is a last-resort fallback: when it would be the display source and the
    // hourly fetch is still in flight, keep shimmering instead of flashing $0.00 → total (SSE-18).
    const resolvedNetSales = (snapshotNetSales != null && snapshotNetSales > 0) ? snapshotNetSales
        : (apiNetSalesNum != null && apiNetSalesNum > 0) ? apiNetSalesNum
        : null;
    const netSales = resolvedNetSales ?? (hourlyTotal > 0 ? hourlyTotal : null);
    const netSalesLoading = kpisLoading || (resolvedNetSales == null && hourlySalesLoading);
    const totalStr = fmtCurrency(netSales ?? 0);

    const displayPeakHour = hourlySalesLoading ? <Bone w={60} h={18} r={4} style={{ margin: '0 auto' }} /> : hourlySalesFailure ? '--' : peakHour;
    // Revenue booked in the peak hour — the hour alone doesn't say how big it was.
    const peakHourSub = hourlySalesLoading || hourlySalesFailure || revenuePeakIndex < 0 ? undefined : fmtCurrency(revenuePeakValue);

    // ── Sales-breakdown waterfall (ZAR-1462) ── real values from snapshotValues,
    // sourced from the same APIs that back the standalone stamp KPIs.
    const waterfallSource: WaterfallSource = orderSource === 'direct' ? 'direct' : orderSource === 'thirdparty' ? 'thirdparty' : 'overall';
    const waterfallData = buildWaterfallData({
        voids: Number(snapshotValues?.voidAmount) || 0,
        discounts: Number(snapshotValues?.discounts) || 0,
        complimentary: Number(snapshotValues?.compAmount) || 0,
        refunds: Number(snapshotValues?.cancelledAmount) || 0,
        netSales: Number(snapshotValues?.netSales) || 0,
        tax: Number(snapshotValues?.tax) || 0,
        tips: Number(snapshotValues?.tips) || 0,
        gratuity: Number(snapshotValues?.gratuity) || 0,
        otherCharges: Number(snapshotValues?.otherCharges) || 0,
        totalCollected: Number(snapshotValues?.grandTotal) || 0,
    });
    const isThirdPartyDrill = waterfallSource === 'thirdparty';
    const primaryLabel = isThirdPartyDrill ? 'Item Total' : 'Net Sales';
    const primaryValue = isThirdPartyDrill ? waterfallData.itemTotal : waterfallData.netSales;
    const totalPaidValue = getTotalPaidValue(waterfallSource, waterfallData);
    const subTitle = `${totalStr} ${isThirdPartyDrill ? 'item total' : 'total revenue'} · ${(exportPeriodFor(activeDateFilter, dateRange)?.label ?? activeDateFilter)}`;

    const inner = loading ? <NetSalesShimmer /> : (<>
        <div style={{ fontSize: 10, color: C.t3, padding: '0 16px 4px' }}>{netSalesLoading ? <Bone w={80} h={12} r={4} /> : subTitle}</div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px 16px' }}>
                <div ref={kpiRowRef} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
                    {[
                        { l: primaryLabel, v: netSalesLoading ? <Bone w={60} h={18} r={4} style={{ margin: '0 auto' }} /> : fmtCurrency(primaryValue), accent: true },
                        { l: getTotalPaidLabel(waterfallSource), v: netSalesLoading ? <Bone w={60} h={18} r={4} style={{ margin: '0 auto' }} /> : fmtCurrency(totalPaidValue), sub: netSalesLoading ? undefined : getTotalPaidSub(waterfallSource) },
                        { l: 'Peak Hour', v: displayPeakHour, sub: peakHourSub },
                        { l: 'Avg/Hour', v: hourlySalesLoading ? <Bone w={60} h={18} r={4} style={{ margin: '0 auto' }} /> : fmtCurrency(avgPerHour) },
                    ].map((k, i) => (
                        <div key={i} style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 12, padding: '10px 12px', textAlign: 'center', boxShadow: C.shadow }}>
                            <div style={{ fontSize: 8, color: C.t3, textTransform: 'uppercase', letterSpacing: 0.4 }}>{k.l}</div>
                            <div style={{ fontSize: 17, fontWeight: 700, color: k.accent ? C.accent : C.t1, marginTop: 3 }}>{k.v}</div>
                            {k.sub && <div style={{ fontSize: 8, color: C.t3, marginTop: 2 }}>{k.sub}</div>}
                        </div>
                    ))}
                </div>
                <div style={{ marginBottom: 12 }}>
                    <SalesWaterfall source={waterfallSource} data={waterfallData} fmt={fmtCurrency} loading={waterfallLoading} onDeductionClick={handleDeductionClick} />
                </div>
                <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 14, padding: '14px 12px', marginBottom: 12, boxShadow: C.shadow }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: C.t1, marginBottom: 8 }}>Hourly Revenue</div>
                    <HourlyBarChart
                        data={hourlyRevenueChartData}
                        loading={hourlySalesLoading}
                        error={hourlySalesFailure}
                        onRetry={() => dispatch(fetchHourlySalesRequest({ locationId, startDate: dateRange.startDate, endDate: dateRange.endDate }))}
                        kpiName="hourly revenue"
                        barColor={C.accent}
                        chartHeight={80}
                        showCurrency={true}
                        minHeight={120}
                        avgOverride={avgPerHour}
                    />
                </div>
        </div>
    </>);

    if (isEmbedded) return <>{inner}</>;
    return (
        <Phone>
            <TopBar variant="mobile" />
            {inner}
            <BottomNav />
        </Phone>
    );
};

export default DrillNetSales;
